import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useState } from 'react';

/**
 * ThemeContext — light / dark for the EGDL tokens.
 *
 * Resolution order: ?theme=light|dark in the URL (persisted), the choice made with the theme
 * switch (ThemeToggle, in the top bar and on the sign-in pages), then the OS preference — with no
 * stored choice the theme follows the OS live. Applied as data-theme on <html> (tokens.css keys on it).
 */
const STORAGE_KEY = 'enablegames_theme';

const ThemeContext = createContext({ theme: 'light', setTheme: () => {}, toggleTheme: () => {} });

function readStored() {
    try {
        const q = new URLSearchParams(window.location.search).get('theme');
        if (q === 'light' || q === 'dark') {
            localStorage.setItem(STORAGE_KEY, q);
            return q;
        }
        const s = localStorage.getItem(STORAGE_KEY);
        return s === 'light' || s === 'dark' ? s : null;
    } catch {
        return null;
    }
}

function systemTheme() {
    return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function ThemeProvider({ children }) {
    const [stored, setStored] = useState(readStored);
    const [system, setSystem] = useState(systemTheme);
    const theme = stored || system;
    // Applied during render (idempotent) so children that resolve token colours
    // in this same render (useTokenColors) already see the new theme.
    if (document.documentElement.dataset.theme !== theme) document.documentElement.dataset.theme = theme;

    useEffect(() => {
        if (!window.matchMedia) return undefined;
        const mq = window.matchMedia('(prefers-color-scheme: dark)');
        const onChange = () => setSystem(mq.matches ? 'dark' : 'light');
        mq.addEventListener('change', onChange);
        return () => mq.removeEventListener('change', onChange);
    }, []);

    useLayoutEffect(() => {
        document.documentElement.dataset.theme = theme;
    }, [theme]);

    const setTheme = useCallback((t) => {
        setStored(t);
        try { localStorage.setItem(STORAGE_KEY, t); } catch { /* private mode */ }
    }, []);
    const toggleTheme = useCallback(() => setTheme(theme === 'dark' ? 'light' : 'dark'), [theme, setTheme]);

    const value = useMemo(() => ({ theme, setTheme, toggleTheme }), [theme, setTheme, toggleTheme]);
    return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
    return useContext(ThemeContext);
}

/**
 * Resolved token colours for canvas / three.js, which can't use var().
 * names: ['accent', 'text-primary', ...] → { accent: '#0F6E8C', ... }, recomputed on theme change.
 */
export function useTokenColors(names) {
    const { theme } = useTheme();
    const key = names.join('|');
    return useMemo(() => {
        const cs = getComputedStyle(document.documentElement);
        const out = {};
        key.split('|').forEach((n) => { out[n] = cs.getPropertyValue('--egdl-' + n).trim(); });
        return out;
        // theme is the invalidation key: tokens.css swaps values under [data-theme]
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key, theme]);
}
